import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@pps/ui/hooks/use-toast";
import { CheckCircle2, Loader2, MapPin } from "lucide-react";
import {
  LessonCheckinError,
  type LessonCheckinErrorCode,
  useLessonCheckin,
} from "@/hooks/useLessonCheckin";

interface CheckinButtonProps {
  lessonId: string;
  lessonDate: string; // YYYY-MM-DD
  startTime: string; // HH:mm[:ss]
  endTime: string; // HH:mm[:ss]
  alreadyCheckedIn?: boolean;
  checkinWindowMinutesBefore?: number;
  className?: string;
}

const ERROR_MESSAGES: Record<LessonCheckinErrorCode, string> = {
  unauthorized: "Voce precisa estar logado para fazer check-in.",
  invalid_body: "Dados invalidos enviados ao servidor.",
  lesson_id_required: "Aula invalida.",
  invalid_lesson_date: "Data da aula invalida.",
  invalid_coordinates: "Coordenadas invalidas.",
  lesson_not_found: "Aula nao encontrada.",
  lesson_inactive: "Esta aula nao esta ativa.",
  date_does_not_match_lesson_schedule: "Esta data nao corresponde ao horario da aula.",
  checkin_only_allowed_today: "Check-in so esta disponivel no dia da aula.",
  checkin_not_open_yet: "Ainda nao e possivel fazer check-in. Volte mais perto do horario.",
  checkin_window_closed: "A janela de check-in ja foi encerrada.",
  student_not_found: "Voce nao esta vinculado a este clube.",
  student_not_active: "Seu cadastro nao esta ativo no clube.",
  not_enrolled_in_lesson: "Voce nao esta matriculado nesta aula.",
  too_far_from_club: "Voce esta longe demais do clube para fazer check-in.",
  checkin_failed: "Nao foi possivel registrar seu check-in.",
  internal_error: "Erro inesperado. Tente novamente em instantes.",
  rate_limited: "Muitas tentativas. Aguarde alguns segundos.",
  method_not_allowed: "Metodo nao permitido.",
  network_error: "Falha de rede. Verifique sua conexao.",
};

function parseTimeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
}

function getCurrentPosition(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 }
    );
  });
}

export function CheckinButton({
  lessonId,
  lessonDate,
  startTime,
  endTime,
  alreadyCheckedIn = false,
  checkinWindowMinutesBefore = 15,
  className,
}: CheckinButtonProps) {
  const { toast } = useToast();
  const { mutateAsync, isPending } = useLessonCheckin();
  const [fetchingLocation, setFetchingLocation] = useState(false);

  // Tick state para re-avaliar `isWithinWindow` a cada 30s. Sem isso, se o
  // usuario abre a pagina 10min antes da janela, o botao fica desabilitado
  // para sempre (useMemo so recalcula quando suas deps mudam, nao com o tempo).
  // IMPORTANTE: `tick` DEVE estar no array de deps do useMemo abaixo — sem
  // isso o fix fica inutil (useMemo nao recalcula mesmo com tick mudando).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const { isWithinWindow, reason } = useMemo(() => {
    // Calcular data/hora no TZ do clube (Brasil). Se usasse UTC, usuario no Brasil
    // teria botao desabilitado no dia correto local (e habilitado no dia errado)
    // nas horas proximas a meia-noite UTC.
    const CLUB_TZ = "America/Sao_Paulo";
    const today = new Date().toLocaleDateString("en-CA", { timeZone: CLUB_TZ });
    if (lessonDate !== today) {
      return { isWithinWindow: false, reason: "Disponivel apenas no dia da aula" };
    }
    // Hora atual no TZ do clube via parse do toLocaleString
    const nowInClubTz = new Date(
      new Date().toLocaleString("en-US", { timeZone: CLUB_TZ })
    );
    const nowMinutes = nowInClubTz.getHours() * 60 + nowInClubTz.getMinutes();
    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = parseTimeToMinutes(endTime);
    if (nowMinutes < startMinutes - checkinWindowMinutesBefore) {
      return { isWithinWindow: false, reason: "Check-in ainda nao aberto" };
    }
    if (nowMinutes > endMinutes) {
      return { isWithinWindow: false, reason: "Janela de check-in encerrada" };
    }
    return { isWithinWindow: true, reason: null };
  }, [lessonDate, startTime, endTime, checkinWindowMinutesBefore, tick]);

  const handleClick = useCallback(async () => {
    setFetchingLocation(true);
    const position = await getCurrentPosition();
    setFetchingLocation(false);

    try {
      const result = await mutateAsync({
        lessonId,
        lessonDate,
        latitude: position?.coords.latitude,
        longitude: position?.coords.longitude,
      });
      toast({
        title: "Check-in realizado",
        description:
          result.attendance.status === "late"
            ? "Presenca registrada como atrasado."
            : "Sua presenca foi registrada.",
      });
    } catch (err) {
      if (err instanceof LessonCheckinError) {
        const base = ERROR_MESSAGES[err.code] ?? "Nao foi possivel fazer check-in.";
        const description =
          err.code === "too_far_from_club" && err.distanceMeters != null
            ? `${base} (${err.distanceMeters} m do clube)`
            : base;
        toast({ title: "Check-in nao realizado", description, variant: "destructive" });
      } else {
        toast({
          title: "Check-in nao realizado",
          description: "Erro inesperado. Tente novamente.",
          variant: "destructive",
        });
      }
    }
  }, [mutateAsync, lessonId, lessonDate, toast]);

  if (alreadyCheckedIn) {
    return (
      <Button disabled variant="outline" className={className}>
        <CheckCircle2 className="mr-2 h-4 w-4" />
        Presenca registrada
      </Button>
    );
  }

  const disabled = !isWithinWindow || isPending || fetchingLocation;

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={className}
      title={reason ?? undefined}
    >
      {isPending || fetchingLocation ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <MapPin className="mr-2 h-4 w-4" />
      )}
      {fetchingLocation
        ? "Obtendo localizacao..."
        : isPending
          ? "Registrando..."
          : isWithinWindow
            ? "Fazer check-in"
            : (reason ?? "Check-in indisponivel")}
    </Button>
  );
}

export default CheckinButton;
